(ns app.front.view.admin.feature-flags
  (:require [app.front.ui :as ui]
            [app.front.ui.section :as section]
            [app.front.ui.updated :as ui-updated]
            [app.front.util :as futil :refer [for-frag]]
            [clojure.string :as str]
            [core.feature-flag.model :as feature-flag-model]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [core.front.href :as href]
            [core.util.util :as util]
            [re-frame.core-instrumented :as rf]))


(rf/reg-event-fx ::toggle-feature-flag
  (fn [{:keys [db]} [_ id enabled?]]
    {:comm/command {:command    :admin/toggle-system-feature
                    :method     :post
                    :data       {:feature-flag/id       id
                                 :feature-flag/enabled? enabled?}
                    :on-success [::toggle-feature-flag.success id enabled?]
                    :notify     {:success :changes-saved}}
     :db           (update db ::state dissoc :modal-data)}))


(rf/reg-event-fx ::toggle-feature-flag.success
  (fn [{:keys [db]} [_ id enabled?]]
    {::ff/toggle [id enabled?]
     :db         (assoc-in db [::state :feature-enablement-times id] [(js/Date.) "me"])}))


(rf/reg-event-fx ::fetch-enabled-features
  (fn [_ _]
    {:comm/command {:command    :admin/enabled-system-features
                    :method     :get
                    :on-success [::fetch-enabled-features.success]}}))


(rf/reg-event-db ::fetch-enabled-features.success
  (fn [db [_ _ feature-enablement-times]]
    (assoc-in db [::state :feature-enablement-times] feature-enablement-times)))


(rf/reg-event-db ::open-modal-for
  (fn [db [_ id description enabled?]]
    (assoc-in db [::state :modal-data] (util/mmap id enabled? description))))


(rf/reg-event-db ::cancel-modal
  (fn [db _]
    (update db ::state dissoc :modal-data)))


(rf/reg-sub ::state :-> ::state)

(rf/reg-sub ::modal-data
  :<- [::state]
  :-> :modal-data)


(rf/reg-sub ::feature-enablement-times
  :<- [::state]
  :-> :feature-enablement-times)

(rf/reg-event-db ::set-search
  (fn [db [_ search]]
    (assoc-in db [::state :search] search)))

(rf/reg-sub ::search
  :<- [::state]
  (fn [state _]
    (get state :search "")))

(rf/reg-event-db ::set-only-unreleased
  (fn [db [_ only-unreleased?]]
    (assoc-in db [::state :only-unreleased?] only-unreleased?)))

(rf/reg-sub ::only-unreleased?
  :<- [::state]
  (fn [state _]
    (get state :only-unreleased? false)))

(rf/reg-sub ::search-term
  :<- [::search]
  (fn [search _]
    (-> search str/trim str/lower-case)))

(defn- matches-search? [search text]
  (str/includes? (str/lower-case (str text)) search))

(rf/reg-sub ::matching-flags
  :<- [::search-term]
  (fn [search _]
    (filterv (fn [{:feature-flag/keys       [id]
                   :feature-flag-model/keys [description jira-issue epic]}]
               (some #(matches-search? search %)
                     [id description jira-issue epic (get feature-flag-model/epic-titles epic)]))
             feature-flag-model/flags)))


(def flag-migrations
  {:feature-flag.id/property-db-units
   [:view/admin.migration.migrate-property-units
    :view/admin.migration.migrate-workspace-address-units]})

(defn- migration-link-text [route]
  (-> (name route)
      (str/replace #"^admin\.(migration\.)?" "")
      (str/replace "-" " ")
      str/capitalize))

(defn- jira-link [issue]
  (cc/link {:href       (str "https://cloudpermit.atlassian.net/browse/" issue)
            :target     "_blank"
            :aria-label (str "Visit " issue)}
           issue))

(defn- flag-row [{:feature-flag/keys       [id]
                  :feature-flag-model/keys [description jira-issue depends-on]
                  :as                      flag}
                 enabled-flags enablement-times search]
  (let [enabled          (contains? enabled-flags id)
        always-enable?   (contains? feature-flag-model/flags-to-always-enable id)
        deps-filled?     (feature-flag-model/deps-filled? flag enabled-flags)
        required-as-dep? (boolean (feature-flag-model/depended-on-by
                                    (assoc flag :feature-flag/enabled? (not enabled)) enabled-flags))
        deps-text        (some->> depends-on
                                  (map #(-> (feature-flag-model/get-model %)
                                            :feature-flag-model/description))
                                  (str/join ", ")
                                  (str " ⇢ Requires: "))]
    [:div {:data-feature-flag (name id)}
     [:div.flex.items-center.gap-4
      [:div
       (cc/checkbox {:id         id
                     :label-text (str description deps-text)
                     :checked    enabled
                     :disabled   (or (and enabled always-enable?) (not deps-filled?) required-as-dep?)
                     :on-change  #(>evt [::open-modal-for id description %])})]
      (when jira-issue [jira-link jira-issue])]
     [:div.ml-8.flex.flex-col.gap-2
      (when (and (seq search) (matches-search? search id))
        [:div.text-xs.font-mono (str id)])
      (when-let [[ts user] (get enablement-times id)]
        [:div.text-xs.italic
         [ui-updated/updated (util/mmap {:verb-tr-key (if enabled
                                                        :update-time-v2/enabled
                                                        :update-time-v2/disabled)}
                                        ts user)]])
      (when (seq (get flag-migrations id))
        [:div.flex.flex-wrap.gap-4
         (for-frag [route (get flag-migrations id)]
           (cc/link {:href       (href/href route)
                     :class-name "text-xs"}
                    (migration-link-text route)))])]]))

(defn- grouped-flags [flags enabled-flags enablement-times search]
  [:div.flex.flex-col.gap-6
   (for-frag [[epic flags] (->> flags
                                (group-by :feature-flag-model/epic)
                                (sort-by (fn [[epic _]]
                                           [(nil? epic) (get feature-flag-model/epic-titles epic) epic])))]
     [:div.bg-blue-50.p-4.rounded-md.border.border-solid.border-ui-dark-gray
      [:h4.mb-2
       (if epic
         [:<> (get feature-flag-model/epic-titles epic) " (" [jira-link epic] ")"]
         "Other feature flags")]
      [:div.flex.flex-col.gap-2
       (for-frag [flag flags]
         [flag-row flag enabled-flags enablement-times search])]])])

(defn view [_]
  (let [tr               (<sub [:tr])
        enabled-flags    (<sub [::ff/enabled-flags])
        enablement-times (<sub [::feature-enablement-times])
        search           (<sub [::search-term])
        searching?       (boolean (seq search))
        only-unreleased? (<sub [::only-unreleased?])
        matching-flags   (<sub [::matching-flags])
        unmatched-count  (- (count feature-flag-model/flags) (count matching-flags))
        visible-flags    (if (and only-unreleased? (not searching?))
                           (remove #(contains? enabled-flags (:feature-flag/id %)) matching-flags)
                           matching-flags)
        always-enable?   #(contains? feature-flag-model/flags-to-always-enable (:feature-flag/id %))
        available-flags  (remove always-enable? visible-flags)
        always-flags     (filter always-enable? visible-flags)
        missing-flags    (remove #(contains? enabled-flags (:feature-flag/id %)) always-flags)
        protected-flags  (filter #(contains? enabled-flags (:feature-flag/id %)) always-flags)]
    [:div.cloudpermit-page.admin-page
     [:div.bx--row.page-heading.justify-between
      [:h1 (tr [:view/admin.feature-flags])]]
     [:div.mb-6.p-6.bg-white.rounded-md.border.border-solid.border-ui-dark-gray
      [:div.flex.flex-wrap.justify-between.items-center
       (cc/checkbox {:id         "show-only-unreleased"
                     :label-text "Show only flags that are disabled (unreleased) in this environment"
                     :checked    only-unreleased?
                     :disabled   searching?
                     :on-change  #(>evt [::set-only-unreleased %])})
       (cc/link {:href (href/href :view/admin.commands.query.exec
                                  {:query-key :enabled-flags-not-in-always-enable})}
                "Report: enabled flags not yet in flags-to-always-enable")]
      [:p.pt-1.text-xs.italic.h-10
       (when searching?
         "Search includes all flags. Clear search to use this filter.")]
      [:div.flex.flex-wrap.items-center.gap-6
       [:div.w-full.lg:w-1-2
        [ui/search {:id                      "feature-flag-search"
                    :label-text              "Search feature flags"
                    :placeholder             "Search by flag ID, description, Jira key, or epic"
                    :close-button-label-text (tr [:search/empty])
                    :value                   (<sub [::search])
                    :on-change               #(rf/dispatch-sync [::set-search (futil/tval %)])
                    :clear-fn                #(>evt [::set-search ""])}]]
       (when searching?
         [:div (str unmatched-count " flags do not match")])]]
     (when (and only-unreleased? (not searching?) (empty? visible-flags))
       [:p.mb-6 "All feature flags are enabled in this environment."])
     (when (and enablement-times (seq missing-flags))
       [:div.mb-6
        (cc/inline-notification
          {:kind              :warning
           :class-name        "max-w-none"
           :hide-close-button true
           :title             "Required feature flags are disabled"
           :subtitle          (str "Disabled flags in flags-to-always-enable: " (count missing-flags) ". "
                                   "Enable them below; they are expected to remain enabled.")})
        [section/section "missing-required-feature-flags" {:tr tr :always-expanded? true}
         "Required flags to enable"
         [grouped-flags missing-flags enabled-flags enablement-times search]]])
     (when (seq available-flags)
       [section/section
        (if searching? "matching-available-feature-flags" "available-feature-flags")
        {:tr tr :always-expanded? searching?}
        "Available feature flags"
        [grouped-flags available-flags enabled-flags enablement-times search]])
     (when (seq protected-flags)
       [section/section (if searching? "matching-always-enabled-feature-flags" "always-enabled-feature-flags")
        {:tr tr :initially-collapsed? (not searching?) :always-expanded? searching?}
        (str "Always-enabled feature flags (" (count protected-flags) ")")
        [:p.mb-4 "These flags are scheduled for removal and cannot be disabled from this view."]
        [grouped-flags protected-flags enabled-flags enablement-times search]])
     (let [{:keys [id enabled? description]} (<sub [::modal-data])]
       (when id
         [ui/confirm-modal
          {:open?        (boolean id)
           :id           "feature-confirm-modal"
           :tr           tr
           :on-close     #(>evt [::cancel-modal])
           :on-yes-click #(>evt [::toggle-feature-flag id enabled?])}
          (str "Are you sure you want to "
               (if enabled? "enable " "disable ")
               description
               "?")]))]))


(def route
  {:name        :view/admin.feature-flags
   :view        view
   :controllers [{:start (fn [_]
                           (>evt [::fetch-enabled-features]))}]})
